/**
 * Change-password page.
 *
 * Reachable when:
 *   - login returned next_step="change_password" (must_change_password=True),
 *     OR
 *   - the operator clicks "change password" from the dashboard.
 *
 * On success the server clears both cookies and revokes every session
 * for the user — caller is logged out and must re-authenticate.
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { asApiError, postChangePassword } from "@/lib/api";
import { useAuth } from "@/lib/use-auth";
import { AuthShell } from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FALLBACK_MIN_LENGTH = 12;

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minLength, setMinLength] = useState<number>(FALLBACK_MIN_LENGTH);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirm) {
      setError("New password and confirmation don't match.");
      return;
    }
    if (newPassword === oldPassword) {
      setError("New password must differ from the old one.");
      return;
    }
    setSubmitting(true);
    try {
      await postChangePassword({
        old_password: oldPassword,
        new_password: newPassword,
      });
      await refresh();
      navigate("/login?changed=1", { replace: true });
    } catch (err) {
      const body = asApiError(err);
      if (body?.min_length && Number.isFinite(body.min_length)) {
        setMinLength(body.min_length);
        setError(
          `Password is too short — minimum length is ${body.min_length} characters.`,
        );
      } else if (body) {
        setError(`Change failed (${body.error}).`);
      } else {
        setError("Change failed. Check your network and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Change your password"
      description={`Set a new password (at least ${minLength} characters). All your other sessions will be signed out.`}
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <div className="space-y-2">
          <Label htmlFor="old">Current password</Label>
          <Input
            id="old"
            type="password"
            autoComplete="current-password"
            required
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="new">New password</Label>
          <Input
            id="new"
            type="password"
            autoComplete="new-password"
            required
            minLength={minLength}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Confirm new password</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={minLength}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
          />
        </div>
        {error ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {error}
          </div>
        ) : null}
        <Button type="submit" className="w-full" disabled={submitting} size="lg">
          <ShieldCheck />
          {submitting ? "Saving…" : "Change password"}
        </Button>
      </form>
    </AuthShell>
  );
}

export default ChangePasswordPage;
